<template>
  <div>
    <b-link class="brand-logo">
      <div class="d-flex mt-2 mb-1 mx-1">
        <add-co-logo />
        <h2 class="brand-text text-primary font-weight-bolder mb-0 ml-1">
          AddCo
        </h2>
      </div>
    </b-link>

    <!-- Loading -->
    <loading
      v-if="isLoading || isSubmitting"
      :active="true"
      :is-full-page="true"
      color="#498ceb"
    />

    <div v-if="!isLoading" class="misc-inner m-2">
      <b-card v-if="clientNotFound">
        <h2 class="text-danger">
          {{ $t('client_not_found_check_url') }}
        </h2>
      </b-card>

      <div v-else>
        <!-- Success message -->
        <b-card v-if="isSubmitted">
          <h4 class="text-success mb-2">
            {{ $t('arsopbl.request_sent_success') }}
          </h4>
          <b-alert show variant="info">
            <strong>{{ $t('arsopbl.case_number') }}:</strong> {{ resultCaseNumber }}
          </b-alert>
          <p>{{ $t('arsopbl.confirmation_email_sent') }}</p>
        </b-card>

        <!-- Form -->
        <form-wizard
          v-if="!isSubmitted"
          color="#498ceb"
          :title="null"
          :subtitle="null"
          layout="horizontal"
          :finish-button-text="$t('message.submit')"
          :back-button-text="$t('message.previous')"
          :next-button-text="$t('message.next')"
          class="wizard-vertical mb-3"
          ref="myForm"
        >
          <template v-slot:footer="slotProps">
            <div class="wizard-footer d-flex justify-content-between align-items-center">
              <b-button
                variant="outline-secondary"
                @click="goToPreviousTab"
                :disabled="slotProps.activeTabIndex === 0"
              >
                &lt; &nbsp; {{ $t('message.previous') }}
              </b-button>

              <b-button
                v-if="!slotProps.isLastStep"
                variant="primary"
                @click="goToNextTab"
              >
                {{ $t('message.next') }} &nbsp; &gt;
              </b-button>

              <b-button
                v-else
                variant="success"
                @click="handleSubmit"
                :disabled="isSubmitting"
              >
                {{ $t('message.submit') }}
              </b-button>
            </div>
          </template>

          <!-- Section 1: Right Selection -->
          <tab-content :title="$t('arsopbl.section_right')">
            <validation-observer ref="rightTab" tag="form">
              <b-row>
                <b-col md="12">
                  <h5 class="mb-2">{{ $t('arsopbl.select_right_title') }}</h5>
                  <p class="mb-2">{{ $t('arsopbl.select_right_subtitle') }}</p>

                  <b-form-group>
                    <validation-provider :name="$t('arsopbl.right')" rules="required" v-slot="{ errors }">
                      <div v-for="right in rights" :key="right.code" class="mb-2">
                        <b-form-radio
                          v-model="selectedRight"
                          :value="right.code"
                          class="font-weight-bold"
                        >
                          {{ $t(right.labelKey) }} ({{ right.code }})
                        </b-form-radio>
                        <small class="ml-4">{{ $t(right.descKey) }}</small>
                      </div>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </validation-observer>
          </tab-content>

          <!-- Section 2: Titular Data -->
          <tab-content :title="$t('arsopbl.section_titular')">
            <validation-observer ref="titularTab" tag="form">
              <b-row>
                <!-- Full name -->
                <b-col md="6">
                  <b-form-group :label="$t('arsopbl.full_name')">
                    <validation-provider :name="$t('arsopbl.full_name')" rules="required" v-slot="{ errors }">
                      <b-form-input
                        v-model="titular.fullName"
                        :placeholder="$t('arsopbl.placeholder_full_name')"
                        trim
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- RUT / Passport -->
                <b-col md="6">
                  <b-form-group :label="$t('arsopbl.rut_passport')">
                    <validation-provider :name="$t('arsopbl.rut_passport')" rules="required" v-slot="{ errors }">
                      <b-form-input
                        v-model="titular.identification"
                        :placeholder="$t('arsopbl.placeholder_rut_passport')"
                        trim
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <!-- Email -->
                <b-col md="6">
                  <b-form-group :label="$t('arsopbl.email')">
                    <validation-provider :name="$t('arsopbl.email')" rules="required|email" v-slot="{ errors }">
                      <b-form-input
                        v-model="titular.email"
                        type="email"
                        :placeholder="$t('arsopbl.placeholder_email')"
                        trim
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Phone -->
                <b-col md="6">
                  <b-form-group :label="$t('arsopbl.phone')">
                    <b-form-input
                      v-model="titular.phone"
                      :placeholder="$t('arsopbl.placeholder_phone')"
                      trim
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <!-- Identity document -->
                <b-col md="6">
                  <b-form-group :label="$t('arsopbl.identity_document')">
                    <validation-provider :name="$t('arsopbl.identity_document')" rules="required" v-slot="{ errors }">
                      <b-button variant="outline-primary" @click="$refs.identityFileInput.click()">
                        <feather-icon icon="UploadIcon" class="mr-50" />
                        {{ $t('arsopbl.select_file') }}
                      </b-button>
                      <input
                        ref="identityFileInput"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style="display: none"
                        @change="onIdentityFileSelected"
                      />
                      <div v-if="identityFile.name" class="mt-1">
                        <feather-icon icon="FileIcon" class="mr-25" />
                        {{ identityFile.name }}
                        <small class="text-muted">({{ formatFileSize(identityFile.size) }})</small>
                      </div>
                      <div v-else class="mt-1">
                        <small class="text-muted">{{ $t('arsopbl.identity_document_hint') }}</small>
                      </div>
                      <input type="hidden" v-model="identityFileValidation" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </validation-observer>
          </tab-content>

          <!-- Section 3: Request Description -->
          <tab-content :title="$t('arsopbl.section_description')">
            <validation-observer ref="descriptionTab" tag="form">
              <b-row>
                <!-- Main description -->
                <b-col md="12">
                  <b-form-group :label="$t('arsopbl.description_label')">
                    <validation-provider :name="$t('arsopbl.description_label')" rules="required" v-slot="{ errors }">
                      <b-form-textarea
                        v-model="requestDescription"
                        :placeholder="$t('arsopbl.placeholder_description')"
                        rows="4"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <!-- Additional info -->
                <b-col md="12">
                  <b-form-group :label="$t('arsopbl.additional_info')">
                    <b-form-textarea
                      v-model="additionalInfo"
                      :placeholder="$t('arsopbl.placeholder_additional_info')"
                      rows="3"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </validation-observer>
          </tab-content>
        </form-wizard>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from '@vue/composition-api'
import { BLink, BButton, BFormInput, BFormTextarea, BFormRadio, BAlert } from 'bootstrap-vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationObserver, ValidationProvider, localize } from 'vee-validate'
import { required, email } from '@validations'
import { useRouter } from '@core/utils/utils'
import AddCoLogo from '@core/layouts/components/Logo.vue'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'
import { useLambda } from '@/views/habit/awsLambda'
import useCommonArsopbl, { RIGHTS } from '@/views/privacy/arsopbl/useCommonArsopbl'

export default {
  components: {
    BLink,
    BButton,
    BFormInput,
    BFormTextarea,
    BFormRadio,
    BAlert,
    FormWizard,
    TabContent,
    ValidationObserver,
    ValidationProvider,
    AddCoLogo,
    Loading,
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { route } = useRouter()
    const { getItem } = useLambda()
    const { createRequest, sendConfirmationEmail, sendNewRequestNotification } = useCommonArsopbl()
    const { clientPublicId } = route.value.params

    const clientData = ref({})
    const clientNotFound = ref(false)
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const isSubmitted = ref(false)
    const resultCaseNumber = ref('')

    // Form data
    const selectedRight = ref('')
    const titular = ref({ fullName: '', identification: '', email: '', phone: '' })
    const identityFile = ref({})
    const requestDescription = ref('')
    const additionalInfo = ref('')
    const rights = RIGHTS

    // Refs for form wizard
    const myForm = ref(null)
    const rightTab = ref(null)
    const titularTab = ref(null)
    const descriptionTab = ref(null)
    const tabRefs = [rightTab, titularTab, descriptionTab]

    const identityFileValidation = computed(() => identityFile.value.name || '')

    const onIdentityFileSelected = (e) => {
      const file = e.target.files[0]
      if (!file) return

      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        showErrorMessage(i18n.t('arsopbl.file_too_large'))
        return
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        showErrorMessage(i18n.t('arsopbl.file_invalid_type'))
        return
      }

      identityFile.value = file
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const validateTab = (tabRef) => new Promise((resolve, reject) => {
      tabRef.value.validate().then(success => {
        if (success) resolve(true)
        else {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
          reject()
        }
      })
    })

    const goToPreviousTab = () => {
      if (myForm.value) myForm.value.prevTab()
    }

    const goToNextTab = async () => {
      const index = myForm.value.activeTabIndex
      try {
        await validateTab(tabRefs[index])
        myForm.value.nextTab()
      } catch (err) {
        // Validation failed
      }
    }

    const handleSubmit = async () => {
      try {
        await validateTab(descriptionTab)
      } catch (err) {
        return
      }

      isSubmitting.value = true

      try {
        const payload = {
          rightCode: selectedRight.value,
          titular: {
            fullName: titular.value.fullName,
            identification: titular.value.identification,
            email: titular.value.email,
            phone: titular.value.phone || null,
          },
          description: requestDescription.value,
          additionalInfo: additionalInfo.value || null,
          clientPublicId: clientPublicId ? Number(clientPublicId) : null,
        }

        const { caseNumber, followUpToken } = await createRequest(
          payload,
          identityFile.value.name ? identityFile.value : null,
          clientData.value._id,
        )

        resultCaseNumber.value = caseNumber

        // Send confirmation email to titular
        sendConfirmationEmail({
          recipientEmail: titular.value.email,
          recipientName: titular.value.fullName,
          caseNumber,
          rightCode: selectedRight.value,
          clientPublicId,
          followUpToken,
        })

        // Notify DPO/Manager
        sendNewRequestNotification({
          caseNumber,
          rightCode: selectedRight.value,
          titularName: titular.value.fullName,
          clientId: clientData.value._id,
        })

        isSubmitted.value = true
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_submit'))
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(async () => {
      try {
        if (!clientPublicId) throw new Error('Client public id not present')

        const item = await getItem({ collection: 'client', query: { publicId: Number(clientPublicId) } })
        if (!item) throw new Error('Client not found')

        item._id = item._id.toString()
        clientData.value = item

        i18n.locale = clientData.value.default_language || 'es'
        localize(clientData.value.default_language || 'es')
      } catch (error) {
        console.log(error)
        clientNotFound.value = true
      } finally {
        isLoading.value = false
      }
    })

    return {
      clientData,
      clientNotFound,
      isLoading,
      isSubmitting,
      isSubmitted,
      resultCaseNumber,
      selectedRight,
      titular,
      identityFile,
      identityFileValidation,
      requestDescription,
      additionalInfo,
      rights,
      required,
      email,
      myForm,
      rightTab,
      titularTab,
      descriptionTab,
      onIdentityFileSelected,
      formatFileSize,
      goToPreviousTab,
      goToNextTab,
      handleSubmit,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-misc.scss';
@import '@core/scss/vue/libs/vue-wizard.scss';
</style>
